import React, { useState, useEffect } from "react";
import {
  Tabs,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
  Table,
  Tag,
  Row,
  Col,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  createMedicine,
  fetchMedicine,
  resetCreateState,
} from "../../Redux/Slice/Patient/Medicine";
import { toast } from "react-toastify";

const { TabPane } = Tabs;
const { Option } = Select;

const Medicine = () => {
  const dispatch = useDispatch();
  const [confirmedMedicines, setConfirmedMedicines] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);

  const [form] = Form.useForm();

  const user = JSON.parse(localStorage.getItem("user"));
  const patientId = user?.patientId;

  const { data, loading, error, success } = useSelector(
    (state) => state.medicine.create
  );

  const { data: fetchData, loading: fetchLoadind } = useSelector(
    (state) => state.medicine.fetch
  );

  useEffect(() => {
    dispatch(fetchMedicine({ patientId }));
  }, [dispatch, patientId]);

  useEffect(() => {
    setConfirmedMedicines(fetchData?.data?.medicines || []);
  }, [fetchData]);

useEffect(() => {
  if (data?.status && success) {
    toast.success(data.message || "Medicines created successfully");
    dispatch(fetchMedicine({ patientId }));
    setIsModalOpen(false);
    form.resetFields();
    dispatch(resetCreateState()); // 👈 reset after success
  } else if (error) {
    toast.error(error.message || "Failed to create medicines");
    dispatch(resetCreateState()); // 👈 reset after error
  }
}, [data, success, error, form, dispatch, patientId]);


  // Table Columns
  const columns = [
    { title: "Medicine", dataIndex: "medicineName", key: "medicineName" },
    { title: "Dosage", dataIndex: "dosage", key: "dosage" },
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    { title: "Order Type", dataIndex: "orderType", key: "orderType" },
    { title: "Frequency", dataIndex: "frequency", key: "frequency" },
    { title: "Days", dataIndex: "noOfDays", key: "noOfDays" },
    {
      title: "Food Instruction",
      dataIndex: "foodInstruction",
      key: "foodInstruction",
    },
    { title: "Created On", dataIndex: "createdOn", key: "createdOn" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status === "Active" ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
  ];

  const handleAddMedicines = (values) => {
    const dosageValue = values.dosageValue
      ? `${values.dosageValue} ${values.dosageUnit}`
      : "";

    const payload = {
      patientId,
      medicines: [
        {
          medicineName: values.medicineName,
          dosage: dosageValue,
          quantity: values.quantity,
          orderType: values.orderType,
          frequency: values.frequency,
          noOfDays: values.noOfDays, // changed days
          foodInstruction: values.foodInstruction,
        },
      ],
    };

    dispatch(createMedicine(payload));
    setEditingMedicine(null); // reset edit mode
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Header with title and Add button */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h2>Medicine Management</h2>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          Add Medicine
        </Button>
      </div>

      {/* Tabs with Medicine Tables */}
      <Tabs defaultActiveKey="1" style={{ marginTop: 20 }}>
        <TabPane tab="Current Medicines" key="1">
          <Table
            dataSource={confirmedMedicines?.filter(
              (med) => med.status === "Active"
            )}
            columns={columns}
            rowKey={(record, index) => index}
            loading={fetchLoadind}
          />
        </TabPane>
        <TabPane tab="Past Medicines" key="2">
          <Table
            dataSource={confirmedMedicines?.filter(
              (med) => med.status === "Inactive"
            )}
            columns={[
              ...columns,
              {
                title: "Action",
                key: "action",
                render: (_, record) => (
                  <Button
                    type="link"
                    onClick={() => {
                      setEditingMedicine(record);
                      form.setFieldsValue({
                        medicineName: record.medicineName,
                        dosageValue: parseInt(record.dosage), // handle "500 mg"
                        dosageUnit: record.dosage.replace(/[0-9 ]/g, ""), // extract unit
                        quantity: record.quantity,
                        orderType: record.orderType,
                        frequency: record.frequency,
                        noOfDays: record.noOfDays,
                        foodInstruction: record.foodInstruction,
                      });
                      setIsModalOpen(true);
                    }}
                  >
                    Add +
                  </Button>
                ),
              },
            ]}
            rowKey={(record, index) => index}
            loading={fetchLoadind}
          />
        </TabPane>
      </Tabs>

      {/* Add Medicine Modal */}
      <Modal
        title={editingMedicine ? "Edit Past Medicine" : "Add Medicines"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingMedicine(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={700}
      >
        <Form form={form} onFinish={handleAddMedicines} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="medicineName"
                label="Medicine Name"
                rules={[{ required: true, message: "Please select medicine" }]}
              >
                <Select placeholder="Select medicine">
                  <Option value="Paracetamol">Paracetamol</Option>
                  <Option value="Amoxicillin">Amoxicillin</Option>
                  <Option value="Ibuprofen">Ibuprofen</Option>
                  <Option value="Cetirizine">Cetirizine</Option>
                  <Option value="Vitamin C">Vitamin C</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Row gutter={8}>
                <Col span={14}>
                  <Form.Item
                    name="dosageValue"
                    label="Dosage"
                    rules={[{ required: true, message: "Enter dosage" }]}
                  >
                    <InputNumber min={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name="dosageUnit"
                    label="Unit"
                    rules={[{ required: true, message: "Select unit" }]}
                  >
                    <Select placeholder="Unit">
                      <Option value="mg">mg</Option>
                      <Option value="ml">ml</Option>
                      <Option value="tablet">Tablet</Option>
                      <Option value="capsule">Capsule</Option>
                      <Option value="drop">Drop</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: "Enter quantity" }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="orderType"
                label="Order Type"
                rules={[{ required: true, message: "Select order type" }]}
              >
                <Select placeholder="Select type">
                  <Option value="Tablet">Tablet</Option>
                  <Option value="Capsule">Capsule</Option>
                  <Option value="Syrup">Syrup</Option>
                  <Option value="Injection">Injection</Option>
                  <Option value="Drops">Drops</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="frequency"
                label="Frequency"
                rules={[{ required: true, message: "Select frequency" }]}
              >
                <Select placeholder="Select frequency">
                  <Option value="1-0-1">1-0-1</Option>
                  <Option value="1-1-1">1-1-1</Option>
                  <Option value="0-1-0">0-1-0</Option>
                  <Option value="1-0-0">1-0-0</Option>
                  <Option value="0-0-1">0-0-1</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="noOfDays"
                label="Number of Days"
                rules={[{ required: true, message: "Enter number of days" }]}
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="foodInstruction"
                label="Food Instruction"
                rules={[{ required: true, message: "Select food instruction" }]}
              >
                <Select placeholder="Select instruction">
                  <Option value="Before food">Before food</Option>
                  <Option value="After food">After food</Option>
                  <Option value="With food">With food</Option>
                  <Option value="Empty stomach">Empty stomach</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Medicine;
